/* eslint-disable i18next/no-literal-string */
import { useState } from "react";

/**
 * Ported from bouncieweb's `LandingPageComponents/FamilyMarketingVideos.js`
 * (plus the `VideoPlayer`, `VideoOverlay`, and `Bento/BentoComponents`
 * `borderRadius` it composed).
 *
 * Two side-by-side marketing videos ("Peace of mind." and "Freedom with
 * safety."), stacked on mobile and row at md. Each tile shows a title + a
 * "Watch video" play button overlay until the viewer starts the video.
 *
 * The original played Wistia-hosted media via `react-player`, which renders
 * Wistia's own iframe player. A native <video> pointed at Wistia's
 * `/embed/medias/<id>.bin` is rejected (HTTP 406), so — matching react-player's
 * actual behavior — we load the Wistia iframe embed on play instead. Media IDs
 * come from bouncieweb's config `wistiaMediaIds`:
 *   storiesMarkAndAndrea16x9 -> 5mnomvuk7c
 *   seniorDriver             -> tl26x9zhz6
 *
 * The original videos were NOT autoplayed (click-to-play behind an overlay), so
 * the custom overlay is preserved and the Wistia player autoplays once clicked.
 *
 * Original breakpoint map (theme/mediaQueries): md 768px -> md:
 */

// borderRadius (Bento/BentoComponents): 20px
const wistiaIframeSrc = (id: string) =>
  `https://fast.wistia.net/embed/iframe/${id}?seo=false&videoFoam=false&autoPlay=1&playsinline=1`;

const videos = [
  { id: "5mnomvuk7c", titleTop: "Peace", titleBottom: "of mind." },
  { id: "tl26x9zhz6", titleTop: "Freedom", titleBottom: "with safety." },
] as const;

type VideoTileProps = {
  id: string;
  titleTop: string;
  titleBottom: string;
};

function VideoTile({ id, titleTop, titleBottom }: VideoTileProps) {
  const [started, setStarted] = useState(false);

  return (
    // Item: flex-grow, rounded-[20px], overflow-hidden
    <div className="relative aspect-video grow overflow-hidden rounded-[20px] bg-[#1F2A3F]">
      {started ? (
        <iframe
          src={wistiaIframeSrc(id)}
          title={`${titleTop} ${titleBottom}`}
          allow="autoplay; fullscreen"
          allowFullScreen
          className="absolute inset-0 h-full w-full border-0"
        />
      ) : (
        // DefaultVideoOverlay: absolute, flex column, items-start, justify-end,
        // cursor-pointer. Clicking swaps in the autoplaying Wistia player.
        <button data-sid={`family-video-start:${id}`}
          type="button"
          onClick={() => setStarted(true)}
          className="absolute inset-0 flex cursor-pointer flex-col items-start justify-end border-none bg-transparent p-0 text-left"
        >
          {/* VideoOverlayTitleWithDefaultLayout: text-medium font, 38px, p-4,
              white with soft shadow */}
          <h3
            className="p-4 text-[38px] leading-none font-medium text-white"
            style={{ textShadow: "0px 0px 6px rgba(0,0,0,0.3)" }}
          >
            {titleTop}
            <br />
            {titleBottom}
          </h3>

          {/* VideoOverlayPlayButtonWithDefaultLayout (needsLessBottomMargin):
              mt-1, mb-6, blue translucent pill, blurred backdrop */}
          <span
            className="mt-1 mb-6 flex items-center justify-center rounded-r px-12 py-2 backdrop-blur-[10px] sm:mt-2"
            style={{ backgroundColor: "rgba(30, 105, 242, 0.8)" }}
          >
            {/* VideoOverlayText */}
            <span className="mr-2 text-[18px] font-medium text-white">
              Watch video
            </span>
            {/* VideoOverlayPlayButtonIcon: 32px white play triangle */}
            <span className="w-8">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M6 4.5v15l13-7.5-13-7.5z" fill="#fff" />
              </svg>
            </span>
          </span>
        </button>
      )}
    </div>
  );
}

export default function FamilyMarketingVideos() {
  return (
    // Layout: flex column on mobile, row at md, gap-6, full width
    <div className="flex w-full flex-col gap-6 md:flex-row">
      {videos.map((video) => (
        <VideoTile key={video.id} {...video} />
      ))}
    </div>
  );
}
